import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Slider } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import API from 'api/API';
import { OnboardingStatus } from 'api/Responses/SatoResponses';
import Modal from 'components/Modal/Modal';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { useLanguage } from 'context/AppContext';
import { SelectedSetSelectors, useSelectedSet } from 'context/SelectedSet';
import { getVideo } from 'services/FileService';
import formatString from 'utils/FormatString';
import { formatDuration } from 'utils/helpers/DateTimeFormat.helpers';
import useReviewVideo from '../hooks/useReviewVideo';
import * as Styles from '../SATOVideosPage.styles';

/**
 * This component has an asynchronous rendering issue because it never gets unmounted.
 *
 * TL;DR; If you select a video, close it before it loads, then select another video
 * in Safari it will load the first video and ignore the second video.
 *
 * We use a ref "videoUrlRef" to control the state of the actual current video, and also
 * to ignore promises that shouldn't change the state. The promises cannot access the
 * state in the next loop and operate longer than each loop (time-wise). Because a ref
 * can change independently of the event loop, we can control these "out of loop"
 * promises using this ref, as it takes the current state out of the loop.
 */

const ReviewVideoModal: React.FC = () => {
  const [language] = useLanguage();

  /**
   * videoTrim[0] is crop_start of video
   * videoTrim[1] is crop_end of video
   */
  const [videoTrim, setVideoTrim] = useState<number[] | undefined>();
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | undefined>(undefined);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoType, setVideoType] = useState<string>();
  const [videoDuration, setVideoDuration] = useState<number>();

  const videoUrlRef = useRef<string | undefined>();

  const { selectedSetState, dispatchSelectedSet } = useSelectedSet();
  const video = useMemo(() => SelectedSetSelectors.selectSelectedVideo(selectedSetState), [selectedSetState]);
  const { reviewVideo, reviewLoading } = useReviewVideo(video);

  const handleVideoDurationChange = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement, Event>): void => {
      const roundedDuration = Math.floor(e.currentTarget.duration * 10) / 10;

      setVideoDuration(roundedDuration);

      // Set video crop times if they exists, otherwise default to min/max video time.
      setVideoTrim(video?.crop_start && video?.crop_end ? [video.crop_start, video.crop_end] : [0, roundedDuration]);
    },
    [video],
  );

  const handleClose = useCallback((): void => {
    dispatchSelectedSet({
      type: 'SET_SELECTED_VIDEO',
      payload: null,
    });

    setVideoTrim(undefined);
    setVideoDuration(undefined);
  }, [dispatchSelectedSet]);

  const handleReview = useCallback(
    async (status: OnboardingStatus.APPROVED | OnboardingStatus.REJECTED): Promise<void> => {
      await reviewVideo(status, videoTrim);
      handleClose();
    },
    [videoTrim, reviewVideo, handleClose],
  );

  useEffect(() => {
    // Set the ref as the current video URL
    videoUrlRef.current = video?.url || undefined;
  }, [video]);

  useEffect(() => {
    if (video?.converted_url || video?.url) {
      setVideoLoading(true);

      // Get the video's firebase download URL...
      const videoUrl = video.converted_url ?? video.url;
      const useConverted = !!video.converted_url;
      getVideo(videoUrl, useConverted)
        // ...then download the video...
        .then(url => axios.get<Blob>(url, { responseType: 'blob' }))
        .then(({ data }) => {
          // check that we are about to load the correct video and not the previous video
          if (video.url === videoUrlRef.current) {
            // ...set the blob...
            setVideoType(data.type);
            setVideoBlobUrl(URL.createObjectURL(data));
            // ...and only stop loading once we've got the correct video.
            setVideoLoading(false);
          }
        })
        .catch(API.errorHandler);
    }
  }, [video]);

  return (
    <Modal open={!!video} close={handleClose} width="65em">
      {video && (
        <>
          <h2>
            {formatString(
              language.Sato.videosPage.reviewVideoModalTitle,
              language.Sato.videoType[video.type],
              `${video.order + 1}`,
            )}
          </h2>
          <Styles.VideoContainer>
            {videoLoading || reviewLoading ? (
              <PageSpinner />
            ) : (
              <Styles.Video controls onDurationChange={handleVideoDurationChange}>
                <source
                  src={videoBlobUrl}
                  type={videoType}
                  onError={() => toast.error(language.Sato.videosPage.videoError)}
                />
              </Styles.Video>
            )}
          </Styles.VideoContainer>
          {videoDuration && (
            <Styles.VideoTrimContainer>
              <p>{language.Sato.videosPage.cropTimes}</p>
              <Slider
                disabled={reviewLoading}
                value={videoTrim}
                onChange={(_, v) => setVideoTrim(v as number[])}
                min={0}
                max={videoDuration}
                step={1}
                valueLabelFormat={formatDuration}
                valueLabelDisplay="on"
              />
              {/* <Slider
								ValueLabelComponent={ReviewVideoLabel}
							/> */}
            </Styles.VideoTrimContainer>
          )}
          <Styles.VideoButtonsContainer>
            <Styles.VideoButton onClick={() => handleReview(OnboardingStatus.APPROVED)} disabled={reviewLoading}>
              {language.Sato.videosPage.approveVideo}
            </Styles.VideoButton>
            <Styles.VideoButton onClick={() => handleReview(OnboardingStatus.REJECTED)} disabled={reviewLoading}>
              {language.Sato.videosPage.rejectVideo}
            </Styles.VideoButton>
          </Styles.VideoButtonsContainer>
        </>
      )}
    </Modal>
  );
};

export default ReviewVideoModal;
